// src/pages/Login/index.js
import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import logoJrRobot from '../../logo_jrrobot.png'; // Importando o logotipo

const SEDLEX_URL = 'https://sedlex.jaimeroriz.pt';

// Dentro do iframe do SedLex o link abriria o SedLex dentro do próprio iframe
const isEmbedded = window.self !== window.top;

const sedlexLink = (
  <a
    href={SEDLEX_URL}
    target="_blank"
    rel="noopener noreferrer"
    style={{ color: 'inherit', fontWeight: 700, textDecoration: 'underline' }}
  >
    sedlex
  </a>
);

const noticeStyle = {
  backgroundColor: '#fff8e1',
  color: '#8a5a00',
  border: '1px solid #f5d98b',
  padding: '0.75rem',
  borderRadius: '6px',
  marginBottom: '1.5rem',
  textAlign: 'center',
  fontWeight: 500
};

const Login = () => {
  const { user, login, loading: authLoading } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // null = a verificar; false = login por senha desativado (variável PASSWORD_LOGIN_DISABLED)
  const [passwordLoginEnabled, setPasswordLoginEnabled] = useState(null);
  // Acesso de emergência: com o login por senha desativado, o link discreto abre o formulário (só administradores entram)
  const [legacyOpen, setLegacyOpen] = useState(false);

  useEffect(() => {
    // Só depois da validação inicial do AuthProvider o axios tem a baseURL certa
    if (authLoading || user) return undefined;

    let active = true;

    axios.get('/api/auth/login-status')
      .then((response) => {
        if (active) setPasswordLoginEnabled(response.data?.passwordLoginEnabled !== false);
      })
      .catch(() => {
        // Sem resposta, vale o padrão: mostrar o login
        if (active) setPasswordLoginEnabled(true);
      });

    return () => {
      active = false;
    };
  }, [authLoading, user]);

  console.log('Login page - user:', user);

  // Se já está logado, redireciona
  if (user) {
    return <Navigate to="/clientdata" replace />;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    console.log('Attempting login with:', formData.email);

    if (!formData.email || !formData.password) {
      setError('Por favor, preencha todos os campos');
      setLoading(false);
      return;
    }

    const result = await login(formData.email, formData.password);

    if (!result.success) {
      setError(result.message);
    }

    setLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: 'linear-gradient(135deg,rgb(82, 111, 240) 0%, #764ba2 100%)',
      padding: '1rem'
    }}>
      <div style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        width: '100%',
        maxWidth: '400px'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          {/* Logotipo adicionado aqui */}
          <img
            src={logoJrRobot}
            alt="JR Robot Logo"
            style={{
              maxWidth: '120px',
              marginBottom: '1rem',
              display: 'block',
              margin: '0 auto 1rem auto'
            }}
          />
          <h1 style={{ color: '#2c3e50', margin: '0 0 0.5rem 0' }}>JR Robot 2.7</h1>
          {passwordLoginEnabled && (
            <p style={{ color: '#666', margin: 0 }}>Faça login para continuar</p>
          )}
        </div>

        {passwordLoginEnabled === null && (
          <p style={{ textAlign: 'center', color: '#666', margin: 0 }}>A carregar...</p>
        )}

        {passwordLoginEnabled === false && (
          <div style={{ ...noticeStyle, marginBottom: 0 }}>
            {isEmbedded
              ? <>Sessão terminada. Reabra o JRRobot a partir do {sedlexLink}.</>
              : <>Este acesso foi desativado. Faça login pelo {sedlexLink}.</>}
          </div>
        )}

        {passwordLoginEnabled === false && (
          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <button
              type="button"
              onClick={() => setLegacyOpen((open) => !open)}
              aria-expanded={legacyOpen}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                color: '#c4c8cf',
                fontSize: '0.75rem',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              Acesso Antigo
            </button>
          </div>
        )}

        {(passwordLoginEnabled || (passwordLoginEnabled === false && legacyOpen)) && (
          <>
            {passwordLoginEnabled && (
              /* Aviso de descontinuação: a entrada passa a ser feita pelo SedLex */
              <div style={noticeStyle}>
                Este acesso será desativado. Faça login pelo {sedlexLink}.
              </div>
            )}

            {!passwordLoginEnabled && (
              <p style={{ textAlign: 'center', color: '#999', fontSize: '0.85rem', margin: '1rem 0' }}>
                Acesso restrito a administradores.
              </p>
            )}

            <form onSubmit={handleSubmit}>
              {error && (
                <div style={{
                  backgroundColor: '#fee',
                  color: '#c33',
                  padding: '0.75rem',
                  borderRadius: '6px',
                  marginBottom: '1rem',
                  textAlign: 'center'
                }}>
                  {error}
                </div>
              )}

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#2c3e50' }}>
                  Email
                </label>
                <input
                  type="text"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e1e5e9',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                  disabled={loading}
                  required
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#2c3e50' }}>
                  Senha
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e1e5e9',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                  disabled={loading}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '0.875rem',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? 'Entrando...' : 'Entrar'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
